import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ReloadOutlined, InboxOutlined } from "@ant-design/icons";
import { Alert, Button, Spin, Typography, Badge } from "antd";
import dayjs from "dayjs";
import { observer } from "mobx-react-lite";
import { reaction } from "mobx";
import { useEffect, useCallback, useMemo, useRef } from "react";
import RouteDateStatusFilters from "./RouteDateStatusFilters";
import { debounce } from "@/utils/debounce";
import RouteCardExpanded from "./RouteCardExpanded";
import { getRootStore } from "@/stores/RootStore";
import { routeService } from "@/services/RouteService";
import styles from "@/styles/Dashboard/ActiveRoutesBlock.module.less";
const { Title } = Typography;
// Интервал автообновления (60 секунд)
const REFRESH_INTERVAL = 60000;
/**
 * Опоздание и признак протухшего прогноза для пары план/ожид.
 * Опоздание считаем от РЕАЛЬНОСТИ (now), а не как expected−planned: при протухшем
 * прогнозе (expected===planned, пайплайн пересчёта не отработал) разница всегда ≈0,
 * и диспетчер не видит, что маршрут опаздывает.
 */
const computeTiming = (plannedTime, expectedTime) => {
    if (!plannedTime)
        return { delayMinutes: null, isForecastStale: false };
    const planned = dayjs(plannedTime);
    const now = dayjs();
    // Самая поздняя из (now, expected) — насколько мы уже за планом
    let latest = now;
    if (expectedTime && dayjs(expectedTime).isAfter(latest))
        latest = dayjs(expectedTime);
    const diff = latest.diff(planned, 'minute');
    const delayMinutes = diff > 0 ? diff : null;
    // Прогноз протух: expected не сдвинулся с плана, хотя реальность уже позже плана
    const isForecastStale = !!expectedTime && dayjs(expectedTime).isSame(planned) && now.isAfter(planned);
    return { delayMinutes, isForecastStale };
};
/** Самое свежее фактическое событие по всему маршруту (последний реальный сигнал). */
const getLastFactTime = (routePoints) => {
    let last = null;
    for (const p of routePoints) {
        for (const t of [p.actualArrivalTime, p.actualDepartureTime]) {
            if (t) {
                const d = dayjs(t);
                if (!last || d.isAfter(last))
                    last = d;
            }
        }
    }
    return last ? last.toISOString() : null;
};
/**
 * Определяет актуальную (текущую) точку маршрута
 * Бизнес-логика:
 * - Первая точка (База старт): техник может только УБЫТЬ (нет actualArrivalTime)
 * - Последняя точка (База финиш): техник может только ПРИБЫТЬ (нет actualDepartureTime)
 * - Средние точки (Заказы): техник ПРИБЫВАЕТ и УБЫВАЕТ
 */
const getCurrentRoutePoint = (routePoints) => {
    if (routePoints.length === 0)
        return null;
    const sorted = [...routePoints].sort((a, b) => a.orderIndex - b.orderIndex);
    const lastIndex = sorted.length - 1;
    const lastFactTime = getLastFactTime(sorted);
    for (let i = 0; i < sorted.length; i++) {
        const point = sorted[i];
        const isFirst = i === 0;
        const isLast = i === lastIndex;
        if (isFirst) {
            // Первая точка (База) — проверяем только убытие
            if (!point.actualDepartureTime) {
                const { delayMinutes, isForecastStale } = computeTiming(point.plannedDepartureTime, point.expectedDepartureTime);
                return {
                    orderIndex: point.orderIndex,
                    status: 'awaiting_departure',
                    message: 'Ожидается убытие с базы',
                    address: point.address,
                    orderNumber: null,
                    plannedTime: point.plannedDepartureTime,
                    expectedTime: point.expectedDepartureTime,
                    delayMinutes,
                    isForecastStale,
                    lastFactTime,
                };
            }
            continue; // Убыл с первой точки — идём дальше
        }
        if (isLast) {
            // Последняя точка (База) — проверяем только прибытие
            if (!point.actualArrivalTime) {
                const { delayMinutes, isForecastStale } = computeTiming(point.plannedArrivalTime, point.expectedArrivalTime);
                return {
                    orderIndex: point.orderIndex,
                    status: 'in_transit',
                    message: 'Ожидается прибытие на базу',
                    address: point.address,
                    orderNumber: null,
                    plannedTime: point.plannedArrivalTime,
                    expectedTime: point.expectedArrivalTime,
                    delayMinutes,
                    isForecastStale,
                    lastFactTime,
                };
            }
            // Прибыл на последнюю точку — маршрут завершён
            return {
                orderIndex: point.orderIndex,
                status: 'completed',
                message: 'Маршрут завершён',
                address: point.address,
                orderNumber: null,
                plannedTime: null,
                expectedTime: null,
                delayMinutes: null,
                isForecastStale: false,
                lastFactTime,
            };
        }
        // Средняя точка (Заказ)
        if (!point.actualArrivalTime) {
            // Ещё не прибыл на заказ
            const { delayMinutes, isForecastStale } = computeTiming(point.plannedArrivalTime, point.expectedArrivalTime);
            return {
                orderIndex: point.orderIndex,
                status: 'in_transit',
                message: point.orderNumber ? `В пути к заказу ${point.orderNumber}` : 'В пути к заказу',
                address: point.address,
                orderNumber: point.orderNumber,
                plannedTime: point.plannedArrivalTime,
                expectedTime: point.expectedArrivalTime,
                delayMinutes,
                isForecastStale,
                lastFactTime,
            };
        }
        if (!point.actualDepartureTime) {
            // Прибыл, но ещё не убыл — работает на заказе
            const { delayMinutes, isForecastStale } = computeTiming(point.plannedDepartureTime, point.expectedDepartureTime);
            return {
                orderIndex: point.orderIndex,
                status: 'at_order',
                message: point.orderNumber ? `На заказе ${point.orderNumber}` : 'На заказе',
                address: point.address,
                orderNumber: point.orderNumber,
                plannedTime: point.plannedDepartureTime,
                expectedTime: point.expectedDepartureTime,
                delayMinutes,
                isForecastStale,
                lastFactTime,
            };
        }
        // Убыл с этой точки — идём к следующей
    }
    // Все точки пройдены — маршрут завершён
    const lastPoint = sorted[lastIndex];
    return {
        orderIndex: lastPoint.orderIndex,
        status: 'completed',
        message: 'Маршрут завершён',
        address: lastPoint.address,
        orderNumber: null,
        plannedTime: null,
        expectedTime: null,
        delayMinutes: null,
        isForecastStale: false,
        lastFactTime,
    };
};
const ActiveRoutesBlock = observer(() => {
    const { dashboardStore } = getRootStore();
    const scrollContainerRef = useRef(null);
    const intervalRef = useRef(null);
    const prevFiltersRef = useRef('');
    // Вычисление диапазона дат на основе фильтра
    // RouteService добавляет T23:59:59.999Z к dateTo, поэтому передаём только дату
    const getDateRange = useCallback(() => {
        const today = dayjs().startOf('day');
        if (dashboardStore.dateFilter === 'today') {
            return {
                dateFrom: today.toISOString(),
                dateTo: today.format('YYYY-MM-DD'), // только дата, без времени
            };
        }
        else {
            const tomorrow = today.add(1, 'day');
            return {
                dateFrom: tomorrow.toISOString(),
                dateTo: tomorrow.format('YYYY-MM-DD'), // только дата, без времени
            };
        }
    }, [dashboardStore.dateFilter]);
    /**
     * Преобразует RouteSearchViewWithPoints в RouteDetailedView
     * Данные уже загружены одним запросом - не требуется дополнительный API вызов
     */
    const mapToDetailedView = useCallback((searchView) => {
        const points = searchView.routePoints || [];
        const completedPoints = points.filter((p) => p.actualDepartureTime);
        const progressPercent = points.length > 0
            ? Math.round((completedPoints.length / points.length) * 100)
            : 0;
        // Расчёт задержки от РЕАЛЬНОСТИ (now vs план первой непройденной точки).
        // Берём первую непройденную точку С плановым прибытием — это пропускает стартовую
        // базу (у неё plannedArrivalTime = null), иначе delay никогда не вычислялся.
        let delayMinutes = null;
        const delayCalcPoint = points.find((p) => !p.actualArrivalTime && p.plannedArrivalTime);
        if (delayCalcPoint?.plannedArrivalTime) {
            const planned = dayjs(delayCalcPoint.plannedArrivalTime);
            const now = dayjs();
            if (now.isAfter(planned)) {
                delayMinutes = now.diff(planned, 'minute');
            }
        }
        const routePoints = points.map((p, index) => {
            // Supabase может вернуть order как объект или массив
            const orderData = Array.isArray(p.order) ? p.order[0] : p.order;
            // Адрес берём из точки маршрута или из заказа
            const address = p.addressShort || orderData?.addressShort || 'Адрес не указан';
            return {
                orderIndex: p.orderIndex ?? index,
                address,
                orderNumber: orderData?.orderNumber || null,
                isBase: !!p.idBase,
                plannedArrivalTime: p.plannedArrivalTime || null,
                actualArrivalTime: p.actualArrivalTime || null,
                plannedDepartureTime: p.plannedDepartureTime || null,
                actualDepartureTime: p.actualDepartureTime || null,
                expectedArrivalTime: p.expectedArrivalTime || null,
                expectedDepartureTime: p.expectedDepartureTime || null,
                isCompleted: !!p.actualDepartureTime,
            };
        });
        // Вычисляем актуальную точку маршрута
        const currentPoint = getCurrentRoutePoint(routePoints);
        return {
            routeId: searchView.routeId,
            routeStatus: searchView.routeStatus,
            technicianFullName: searchView.technicianFullName,
            technicianId: searchView.technicianId,
            serviceVehicleName: searchView.serviceVehicleName,
            chargingStationName: searchView.chargingStationName,
            ordersCount: searchView.ordersCount,
            // Начало маршрута
            plannedStartTime: searchView.plannedStartTime,
            expectedStartTime: searchView.expectedStartTime,
            actualStartTime: searchView.actualStartTime,
            // Окончание маршрута
            plannedEndTime: searchView.plannedEndTime,
            expectedEndTime: searchView.expectedEndTime,
            actualEndTime: searchView.actualEndTime,
            // Дополнительные данные
            routePoints,
            progressPercent,
            delayMinutes,
            estimatedCompletionTime: searchView.expectedEndTime,
            currentPoint,
        };
    }, []);
    // Загрузка маршрутов с сохранением позиции скролла
    const loadRoutes = useCallback(async (clearBeforeLoad = false) => {
        // Сохраняем позицию скролла
        const scrollTop = scrollContainerRef.current?.scrollTop ?? 0;
        // При смене фильтров очищаем данные для корректного отображения бейджей
        if (clearBeforeLoad) {
            dashboardStore.clearRoutes();
        }
        dashboardStore.setRoutesLoading(true);
        dashboardStore.setRoutesError(null);
        try {
            const dateRange = getDateRange();
            const statusFilters = dashboardStore.routeStatusFiltersArray;
            // Загрузка маршрутов с точками одним запросом (устранение N+1)
            const result = await routeService.listWithPoints({
                routeStatus: statusFilters,
                routeDate: dateRange,
            }, 1, 50); // Лимит на дашборде
            // Преобразование в RouteDetailedView (синхронно, без дополнительных запросов)
            const detailedRoutes = result.data.map((route) => mapToDetailedView(route));
            // Умное обновление через store
            dashboardStore.smartUpdateRoutes(detailedRoutes);
        }
        catch (err) {
            dashboardStore.setRoutesError(err instanceof Error ? err.message : "Ошибка загрузки маршрутов");
        }
        finally {
            dashboardStore.setRoutesLoading(false);
            // Восстанавливаем позицию скролла (в следующем тике после рендера)
            requestAnimationFrame(() => {
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollTop;
                }
            });
        }
    }, [dashboardStore, getDateRange, mapToDetailedView]);
    // Дебаунсинг для фильтров (300ms)
    const debouncedLoadRoutes = useMemo(() => debounce(() => loadRoutes(true), 300), [loadRoutes]);
    // Загрузка при монтировании и настройка автообновления
    useEffect(() => {
        loadRoutes();
        // Автообновление
        intervalRef.current = setInterval(() => {
            loadRoutes();
        }, REFRESH_INTERVAL);
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [loadRoutes]);
    // Реакция на изменение фильтров с дебаунсом
    useEffect(() => {
        const disposer = reaction(() => [
            dashboardStore.dateFilter,
            dashboardStore.routeStatusFiltersArray.join(','),
        ], (newFilters) => {
            const filtersKey = newFilters.join('|');
            // Пропускаем первый вызов (инициализация)
            if (prevFiltersRef.current && prevFiltersRef.current !== filtersKey) {
                debouncedLoadRoutes();
            }
            prevFiltersRef.current = filtersKey;
        });
        return () => disposer();
    }, [dashboardStore, debouncedLoadRoutes]);
    // Обработчик выбора маршрута
    const handleRouteClick = (routeId) => {
        dashboardStore.selectRoute(routeId);
    };
    // Данные из store
    const routes = dashboardStore.routesArray;
    const isLoading = dashboardStore.routesLoading;
    const error = dashboardStore.routesError;
    return (_jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.header, children: [_jsx(Badge, { count: dashboardStore.routesCount, overflowCount: 99, children: _jsx(Title, { level: 5, className: styles.title, children: "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0435 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u044B" }) }), _jsx(Button, { type: "text", icon: _jsx(ReloadOutlined, {}), onClick: () => loadRoutes(), loading: isLoading, title: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })] }), _jsx("div", { className: styles.filters, children: _jsx(RouteDateStatusFilters, {}) }), _jsxs("div", { className: styles.content, ref: scrollContainerRef, children: [error && (_jsx(Alert, { message: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0437\u0430\u0433\u0440\u0443\u0437\u043A\u0438", description: error, type: "error", showIcon: true, closable: true, onClose: () => dashboardStore.setRoutesError(null), action: _jsx(Button, { size: "small", onClick: () => loadRoutes(), children: "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C" }), className: styles.alert })), isLoading && !error && routes.length === 0 && (_jsx("div", { className: styles.loadingContainer, children: _jsx(Spin, { tip: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u043E\u0432..." }) })), !isLoading && !error && routes.length === 0 && (_jsxs("div", { className: styles.emptyState, children: [_jsx(InboxOutlined, { className: styles.emptyIcon }), _jsx("p", { children: "\u041D\u0435\u0442 \u0430\u043A\u0442\u0438\u0432\u043D\u044B\u0445 \u043C\u0430\u0440\u0448\u0440\u0443\u0442\u043E\u0432" })] })), routes.length > 0 && (_jsx("div", { className: styles.routesList, children: routes.map((route) => (_jsx(RouteCardExpanded, { route: route, isSelected: dashboardStore.selectedRouteId === route.routeId, isUpdated: dashboardStore.isRouteUpdated(route.routeId), onClick: () => handleRouteClick(route.routeId) }, route.routeId))) }))] })] }));
});
ActiveRoutesBlock.displayName = "ActiveRoutesBlock";
export default ActiveRoutesBlock;
